import { EventResultFieldDef, EventStanding } from './models';

/**
 * Which column the standings are ordered by.
 *
 * A plain string rather than a tagged union so it can be compared directly in a template
 * and used as a track key: 'rank', 'record', or 'field:<result field id>'.
 */
export type StandingsSortKey = string;

export const RANK_SORT: StandingsSortKey = 'rank';
export const RECORD_SORT: StandingsSortKey = 'record';

export function fieldSortKey(field: EventResultFieldDef): StandingsSortKey {
    return `field:${field.id}`;
}

export type SortDirection = 'asc' | 'desc';

export interface StandingsSortOption {
    key: StandingsSortKey;
    label: string;
    /**
     * Which way round this column is useful first. Rank reads best-first, so ascending;
     * every score reads most-first, so descending. Without this, tapping "Victory Points"
     * would show whoever scored fewest, which is never what was wanted.
     */
    defaultDirection: SortDirection;
}

/**
 * The columns the standings can be ordered by, in display order.
 *
 * Deliberately offers no "tournament points" option: rank already IS tournament points
 * plus the organiser's tiebreakers, so a points chip would duplicate it and could only
 * ever disagree with it on a tie. The points VALUE is still shown on every row -- it is
 * what rank is computed from -- it just is not separately sortable.
 *
 * Labels are passed in rather than translated here so this stays a pure function; the
 * result fields bring their own organiser-authored labels.
 */
export function standingsSortOptions(
    resultFields: EventResultFieldDef[],
    labels: { rank: string; record: string }
): StandingsSortOption[] {
    return [
        { key: RANK_SORT, label: labels.rank, defaultDirection: 'asc' },
        ...resultFields.map((field) => ({
            key: fieldSortKey(field),
            label: field.label,
            defaultDirection: 'desc' as SortDirection
        })),
        { key: RECORD_SORT, label: labels.record, defaultDirection: 'desc' as SortDirection }
    ];
}

/** The number a given column sorts on. Unknown keys fall back to rank. */
function sortValue(standing: EventStanding, key: StandingsSortKey): number {
    if (key === RANK_SORT) {
        return standing.rank;
    }
    if (key === RECORD_SORT) {
        return standing.wins;
    }
    if (key.startsWith('field:')) {
        return standing.resultTotals?.[key.slice('field:'.length)] ?? 0;
    }
    return standing.rank;
}

/**
 * Standings ordered by one column.
 *
 * Returns a NEW array -- the caller holds the unsorted list in a signal, and sorting in
 * place would mutate it and lose the server's original order.
 *
 * Does NOT renumber rank. Rank is the official placing, computed server-side from
 * tournament points and the organiser's tiebreaker fields; sorting by victory points may
 * well put "#4" at the top of the list, and that is the useful answer ("fourth overall,
 * but leads on VP"). Renumbering would invent a second standings order that disagrees
 * with the real one.
 *
 * Ties break on rank ascending, so equal scores stay in official order rather than in
 * whatever order the server happened to return.
 */
export function sortStandings(standings: EventStanding[], key: StandingsSortKey, direction: SortDirection): EventStanding[] {
    const factor = direction === 'asc' ? 1 : -1;
    return [...standings].sort((a, b) => {
        const diff = (sortValue(a, key) - sortValue(b, key)) * factor;
        if (diff !== 0) {
            return diff;
        }
        // Draws separate two players on the same number of wins before falling through.
        if (key === RECORD_SORT && a.draws !== b.draws) {
            return (a.draws - b.draws) * factor;
        }
        return a.rank - b.rank;
    });
}
